import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { track } from "@/lib/analytics";
import { z } from "zod";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { usd } from "@/lib/pricing";
import { optimizeImg, srcSet } from "@/lib/img";
import { Button } from "@/components/ui/button";
import { Check, MapPin, Users, Fuel, Cog, CalendarCheck, CalendarX } from "lucide-react";
import { VehicleFaq } from "@/components/VehicleFaq";

const carQuery = (id: string) => queryOptions({
  queryKey: ["car", id],
  queryFn: async () => {
    const { data, error } = await supabase.from("cars").select("*").eq("id", id).eq("active", true).maybeSingle();
    if (error) throw error;
    if (!data) throw notFound();
    return data;
  },
});

const settingsHoursQuery = queryOptions({
  queryKey: ["settings", "hours"],
  queryFn: async () => {
    const { data } = await supabase.from("settings").select("business_hours").eq("id", 1).maybeSingle();
    return data;
  },
});

const carRangesQuery = (id: string) => queryOptions({
  queryKey: ["car-ranges", id],
  queryFn: async () => {
    const { data, error } = await supabase.rpc("get_unavailable_ranges");
    if (error) throw error;
    return (data ?? []).filter((r: any) => r.car_id === id) as { start_date: string; end_date: string }[];
  },
});

const searchSchema = z.object({
  start: fallback(z.string().optional(), undefined),
  end: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/cars/$carId")({
  validateSearch: zodValidator(searchSchema),
  head: ({ params, loaderData }) => {
    const car = (loaderData as any)?.[0] as
      | { year: number; make: string; model: string; description?: string | null; daily_rate: number | string; photos?: string[] | null }
      | undefined;
    const url = `https://creedcarental.com/cars/${params.carId}`;
    const title = car ? `${car.year} ${car.make} ${car.model} — Rent in Miami | Creed` : "Vehicle — Creed Car Rental Miami";
    const description = car
      ? (car.description?.slice(0, 155) ||
        `Rent a ${car.year} ${car.make} ${car.model} in Miami from Creed Car Rental. Delivery to MIA, hotels & Airbnbs. Insurance included.`)
      : "Reserve this luxury vehicle from Creed Car Rental. Miami delivery, insurance included, transparent pricing.";
    const image = car?.photos?.[0] || undefined;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "product" },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:image", content: image });
    }
    const scripts: Array<Record<string, any>> = [];
    if (car) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: `${car.year} ${car.make} ${car.model}`,
          image: image ? [image] : undefined,
          description,
          brand: { "@type": "Brand", name: car.make },
          offers: {
            "@type": "Offer",
            priceCurrency: "USD",
            price: String(car.daily_rate),
            url,
            availability: "https://schema.org/InStock",
          },
        }),
      });
    }
    return { meta, links: [{ rel: "canonical", href: url }], scripts };
  },
  loader: ({ context, params }) => Promise.all([
    context.queryClient.ensureQueryData(carQuery(params.carId)),
    context.queryClient.ensureQueryData(carRangesQuery(params.carId)),
  ]),
  component: CarDetail,
  errorComponent: () => <div className="p-10 text-center">Failed to load.</div>,
  notFoundComponent: () => (
    <div className="min-h-screen bg-cream"><SiteHeader />
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display text-4xl text-espresso">Vehicle not found</h1>
        <Button asChild className="mt-6"><Link to="/fleet">Back to fleet</Link></Button>
      </div><SiteFooter /></div>
  ),
});

function CarDetail() {
  const { carId } = Route.useParams();
  const search = Route.useSearch();
  const { data: car } = useSuspenseQuery(carQuery(carId));
  const { data: settings } = useSuspenseQuery(settingsHoursQuery);
  const { data: ranges } = useSuspenseQuery(carRangesQuery(carId));
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    track("view_item", {
      currency: "USD",
      value: Number(car.daily_rate) || 0,
      items: [{
        item_id: car.id,
        item_name: `${car.year} ${car.make} ${car.model}`,
        item_brand: car.make,
        item_category: car.trim || undefined,
        price: Number(car.daily_rate) || 0,
      }],
    });
  }, [car.id, car.make, car.model, car.year, car.trim, car.daily_rate]);


  const photos = car.photos ?? [];
  const weeklyPct = Number(car.weekly_discount_pct) || 0;
  const monthlyPct = Number(car.monthly_discount_pct) || 0;

  const datesActive = !!(search.start && search.end && search.start <= search.end);
  const conflictForDates =
    datesActive && ranges.some(r => search.start! <= r.end_date && r.start_date <= search.end!);

  const bookingSearch = datesActive ? { start: search.start, end: search.end } : {};

  return (
    <div className="min-h-screen bg-cream pb-24 md:pb-0">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-16 md:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-7xl px-6">
          <Link to="/fleet" search={bookingSearch} className="text-sm text-cream/60 hover:text-accent">← Back to fleet</Link>
          <div className="mt-4 flex flex-col gap-1">
            <div className="text-xs uppercase tracking-[0.3em] text-accent">{car.year}</div>
            <h1 className="font-display text-4xl text-cream md:text-5xl">{car.make} {car.model}</h1>
            {car.trim && <div className="text-cream/70">{car.trim}</div>}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div className="overflow-hidden rounded-lg bg-muted aspect-[16/10]">
              {photos[activePhoto] ? (
                <img
                  src={optimizeImg(photos[activePhoto], { width: 1200 })}
                  srcSet={srcSet(photos[activePhoto], [600, 900, 1200, 1600])}
                  sizes="(min-width: 1024px) 60vw, 100vw"
                  alt={`${car.make} ${car.model}`}
                  width={1200}
                  height={750}
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : <div className="grid h-full place-items-center text-muted-foreground">No image</div>}
            </div>
            {photos.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-3">
                {photos.slice(0, 5).map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhoto(i)}
                    className={`aspect-square w-full overflow-hidden rounded transition ${i === activePhoto ? "ring-2 ring-accent" : "opacity-80 hover:opacity-100"}`}
                    aria-label={`View photo ${i + 1}`}
                  >
                    <img src={optimizeImg(p, { width: 200 })} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-2">
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-display text-4xl text-accent">{usd(Number(car.daily_rate))}</span>
              <span className="text-muted-foreground">/ day</span>
            </div>
            {(weeklyPct > 0 || monthlyPct > 0) && (
              <div className="mt-2 text-sm text-espresso/70">
                {weeklyPct > 0 && <>Save {weeklyPct}% on 7+ days</>}
                {weeklyPct > 0 && monthlyPct > 0 && " · "}
                {monthlyPct > 0 && <>Save {monthlyPct}% on 30+ days</>}
              </div>
            )}

            {datesActive && (
              <div className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${
                conflictForDates ? "bg-destructive/10 text-destructive" : "bg-accent/15 text-espresso"
              }`}>
                {conflictForDates
                  ? <><CalendarX className="h-3.5 w-3.5" /> Booked for {search.start} → {search.end}</>
                  : <><CalendarCheck className="h-3.5 w-3.5" /> Available for {search.start} → {search.end}</>}
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm text-espresso">
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-accent" /> {car.location || "Miami delivery available"}</div>
              {car.seats && <div className="flex items-center gap-2"><Users className="h-4 w-4 text-accent" /> {car.seats} seats</div>}
              {car.transmission && <div className="flex items-center gap-2"><Cog className="h-4 w-4 text-accent" /> {car.transmission}</div>}
              {car.fuel_type && <div className="flex items-center gap-2"><Fuel className="h-4 w-4 text-accent" /> {car.fuel_type}</div>}
            </div>

            {car.description && <p className="mt-6 text-espresso/80 leading-relaxed">{car.description}</p>}

            {car.features?.length > 0 && (
              <ul className="mt-6 space-y-2">
                {car.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-espresso"><Check className="h-4 w-4 text-accent" /> {f}</li>
                ))}
              </ul>
            )}

            <div className="mt-8 rounded-lg border border-border bg-card p-4 text-sm">
              <div className="font-semibold text-espresso">Included</div>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                <li>• Comprehensive insurance</li>
                <li>• 24/7 roadside assistance</li>
                <li>• Delivery across Miami-Dade</li>
              </ul>
              <div className="mt-3 font-semibold text-espresso">Not included</div>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                <li>• Tolls &amp; traffic tickets</li>
                <li>• Cleaning &amp; late return fees</li>
              </ul>
            </div>

            <Button asChild size="lg" className="mt-8 hidden md:flex w-full bg-primary hover:bg-primary/90">
              <Link to="/booking/$carId" params={{ carId: car.id }} search={bookingSearch}>Request booking</Link>
            </Button>
            {Number(car.security_deposit) > 0 && (
              <div className="mt-3 hidden md:block text-center text-xs text-muted-foreground">Refundable security deposit: {usd(Number(car.security_deposit))}</div>
            )}
          </div>
        </div>
      </section>

      <VehicleFaq car={car} businessHours={settings?.business_hours} />

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-card/95 backdrop-blur px-4 py-3 flex items-center gap-3">
        <div className="flex-1">
          <div className="font-display text-lg text-accent leading-none">{usd(Number(car.daily_rate))}<span className="text-xs text-muted-foreground"> / day</span></div>
          {Number(car.security_deposit) > 0 && (
            <div className="text-[10px] text-muted-foreground mt-0.5">+ {usd(Number(car.security_deposit))} refundable deposit</div>
          )}
        </div>
        <Button asChild className="bg-primary hover:bg-primary/90">
          <Link to="/booking/$carId" params={{ carId: car.id }} search={bookingSearch}>Request booking</Link>
        </Button>
      </div>

      <SiteFooter />
    </div>
  );
}
